import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { Avatar, Dropdown } from 'antd';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys, menteeProfileQueryKeys, companyProfileQueryKeys } from '../../../lib/queryClient';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  BriefcaseIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import NotificationBell from '../NotificationBell';

const Header: React.FC = () => {
  const { user, logout, isAuthenticated, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const role = user?.role;

  const isMenuActive = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/');

  const { data: profileData } = useQuery({
    queryKey:
      role === 'Mentor' ? mentorProfileQueryKeys.profile :
      role === 'Mentee' ? menteeProfileQueryKeys.profile :
      role === 'Company' ? companyProfileQueryKeys.profile : ['header-profile-skip'],
    queryFn: async () => {
      if (role === 'Mentor') {
        const r = await apiService.getMentorProfile();
        if (!r.success) throw new Error(r.message);
        return r.data;
      }
      if (role === 'Mentee') {
        const r = await apiService.getMenteeProfile();
        if (!r.success) throw new Error(r.message);
        return r.data;
      }
      if (role === 'Company') {
        const r = await apiService.getCompanyProfile();
        if (!r.success) throw new Error(r.message);
        return r.data;
      }
      return null;
    },
    enabled: isAuthenticated && (role === 'Mentor' || role === 'Mentee' || role === 'Company'),
    staleTime: 1000 * 60 * 5,
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePanelClick = () => {
    if (user?.role === 'Mentor') {
      navigate('/profile');
    } else if (user?.role === 'Mentee') {
      navigate('/mentee-profile');
    } else if (user?.role === 'Company') {
      navigate('/company-panel');
    } else if (user?.role === 'Admin') {
      navigate('/admin/panel');
    } else {
      navigate('/profile'); // Default fallback
    }
  };

  // Public menü öğeleri (herkese görünür)
  const getPublicMenuItems = () => {
    return [
      { to: '/', label: 'Anasayfa', icon: HomeIcon },
      { to: '/events', label: 'Etkinlikler', icon: CalendarDaysIcon },
      { to: '/mentors', label: 'Mentörler', icon: UserGroupIcon },
      { to: '/job-postings', label: 'İş İlanları', icon: BriefcaseIcon }
    ];
  };

  // Role göre ek menü öğeleri (sadece giriş yapmış kullanıcılar için)
  const getRoleSpecificMenuItems = () => {
    if (!user) return [];

    switch (user.role) {
      case 'Company':
        return [
          { to: '/mentees', label: 'Mentee\'ler', icon: UserGroupIcon }
        ];
      default:
        return [];
    }
  };

  return (
    <header className="app-public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-4" style={{ textDecoration: 'none' }}>
              <div
                className="flex shrink-0 items-center justify-center"
                style={{
                  boxSizing: 'border-box',
                  width: 48,
                  height: 48,
                  paddingLeft: 10,
                  paddingRight: 10,
                  backgroundColor: 'var(--primary-600)',
                  borderRadius: '50%',
                  boxShadow: 'var(--shadow-glow)',
                }}
              >
                <span
                  className="text-white font-bold"
                  style={{ fontSize: 16, lineHeight: 1, letterSpacing: '-0.02em' }}
                >
                  M
                </span>
              </div>
              <span
                className="text-xl font-bold tracking-tight"
                style={{ color: 'var(--text-primary)', letterSpacing: '-0.03em' }}
              >
                Menteem
              </span>
            </Link>
          </div>

          {/* Navigation - Public menü herkese görünür */}
          <nav className="hidden md:flex space-x-8">
            {/* Public menü öğeleri */}
            {getPublicMenuItems().map((item, index) => {
              const IconComponent = item.icon;
              const active = isMenuActive(item.to);
              return (
                <Link
                  key={index}
                  to={item.to}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium"
                  style={{
                    color: active ? 'var(--primary-600)' : 'var(--text-secondary)',
                    fontWeight: active ? 600 : 500,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.color = 'var(--primary-600)'; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'; }}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Role-specific menü öğeleri (sadece giriş yapmış kullanıcılar için) */}
            {isAuthenticated && getRoleSpecificMenuItems().map((item, index) => {
              const IconComponent = item.icon;
              const active = isMenuActive(item.to);
              return (
                <Link
                  key={`role-${index}`}
                  to={item.to}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium"
                  style={{
                    color: active ? 'var(--primary-600)' : 'var(--text-secondary)',
                    fontWeight: active ? 600 : 500,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.color = 'var(--primary-600)'; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'; }}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <NotificationBell />
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'panel',
                        label: 'Panel',
                        icon: <UserIcon className="w-4 h-4" />,
                        onClick: handlePanelClick
                      },
                      {
                        key: 'logout',
                        label: isLoggingOut ? 'Çıkış yapılıyor...' : 'Çıkış Yap',
                        icon: <ArrowRightOnRectangleIcon className="w-4 h-4" />,
                        onClick: handleLogout,
                        disabled: isLoggingOut
                      }
                    ]
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <div
                    className="flex items-center cursor-pointer px-4 py-2 rounded-[10px] transition-colors"
                    style={{ border: '1px solid transparent' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--neutral-100)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-light)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                      (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
                    }}
                  >
                    <Avatar
                      size={40}
                      style={{ 
                        backgroundColor: 'var(--primary-600)', 
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: 'bold',
                      }}
                      src={
                        user?.role === 'Company'
                          ? ((profileData as { logo?: string })?.logo ? `data:image/jpeg;base64,${(profileData as { logo: string }).logo}` : undefined)
                          : ((profileData as { profileImage?: string })?.profileImage ? `data:image/jpeg;base64,${(profileData as { profileImage: string }).profileImage}` : undefined)
                      }
                    >
                      {profileData
                        ? (user?.role === 'Company'
                          ? ((profileData as { logo?: string; companyName?: string }).logo ? undefined : (profileData as { companyName?: string }).companyName?.charAt(0)?.toUpperCase())
                          : `${(profileData as { firstName?: string; lastName?: string }).firstName?.charAt(0)}${(profileData as { firstName?: string; lastName?: string }).lastName?.charAt(0)}`)
                        : (user ? (user.fullName || user.name).charAt(0) : 'U')}
                    </Avatar>
                    <div className="text-left pl-8" style={{ cursor: 'pointer' }}>
                      <div className="text-sm font-medium" style={{ marginLeft:10,marginRight:5,
                         color: 'var(--text-primary)', cursor: 'pointer' }}>
                        {profileData
                          ? (user?.role === 'Company'
                            ? (profileData as { companyName?: string; fullName?: string }).companyName || (profileData as { companyName?: string; fullName?: string }).fullName
                            : `${(profileData as { firstName?: string; lastName?: string }).firstName} ${(profileData as { firstName?: string; lastName?: string }).lastName}`)
                          : (user?.role === 'Company' ? (user.fullName || user.name) : user?.name)}
                      </div>
                      <div
                        className="text-xs truncate"
                        style={{
                          marginLeft: 10,
                          marginRight: 5,
                          maxWidth: 180,
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                        title={user?.email || (profileData as { email?: string })?.email}
                      >
                        {user?.email || (profileData as { email?: string })?.email || (
                          user?.role === 'Mentor' ? 'Mentor' :
                          user?.role === 'Mentee' ? 'Mentee' :
                          user?.role === 'Company' ? 'Şirket' :
                          user?.role === 'Admin' ? 'Admin' : 'Kullanıcı'
                        )}
                      </div>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 pl-8" style={{ color: 'var(--text-secondary)', cursor: 'pointer' }} />
                  </div>
                </Dropdown>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="px-3 py-2 rounded-md text-sm font-medium"
                  style={{ 
                    color: 'var(--text-secondary)',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--primary-600)'}
                  onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--text-secondary)'}
                >
                  Giriş Yap
                </Link>
                <Link
                  to="/register-mentee"
                  className="px-4 py-2 rounded-md text-sm font-medium text-white"
                  style={{ 
                    backgroundColor: 'var(--accent-500)',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'var(--accent-600)'}
                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'var(--accent-500)'}
                >
                  Kayıt Ol
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
